import { evaluateFilterQuery, type FilterQuery } from '@viewer/lib/filter'
import type { SelectionLike } from './clashSets'

// ---------------------------------------------------------------------------
// Regras dos conjuntos: casca fina por cima do motor de filtros.
//
// O motor e o do Shift+F (`@viewer/lib/filter`), sem alteracoes. O que
// esta camada acrescenta e o que a deteccao de conflitos precisa e o
// painel Filtro nao:
//
//   - CUSTO. Correr uma regra e varrer os modelos uma vez POR REGRA
//     (`classifyDimension` nao tem cache por criterio). Aqui mede-se
//     sempre, para a UI poder dizer quanto custou e para nao se avancar
//     as cegas em modelos grandes.
//   - CACHE por (regra, modelos). A contagem em vivo re-avaliava a mesma
//     regra a cada render do painel; com a cache, mexer num campo que
//     nao e a regra (tolerancia, folga, agrupamento) nao volta a varrer
//     modelo nenhum. E a mesma regra no lado A e no lado B custa uma
//     passagem, nao duas.
//   - CHAVE ESTAVEL da regra, para memos e para saber se mudou mesmo.
//
// A cache e invalidada pela chave dos modelos carregados: trocar de IFC
// deita fora tudo o que la estiver.
// ---------------------------------------------------------------------------

export interface ClashRuleOutcome {
  targets: SelectionLike[]
  /** Milissegundos que a avaliacao demorou. */
  ms: number
  /** Veio da cache (custo zero). */
  cached: boolean
}

/** Chave estavel de uma regra: so o que muda o resultado. A ordem das
 *  regras nao muda o resultado (E e OU sao comutativos), mas manter a
 *  ordem do utilizador e mais barato do que ordenar e nao custa
 *  reavaliacoes a mais na pratica. */
export function ruleKey(query: FilterQuery | null | undefined): string {
  if (!query) return ''
  const rules: string[] = []
  for (const r of query.rules) {
    const propertyKey =
      r.dimension === 'property' ? (r.propertyKey ?? '').trim() : ''
    // Mesma condicao de validade do `evaluateFilterQuery`: uma regra sem
    // valores (ou uma propriedade sem chave) nao entra na conta, por isso
    // tambem nao pode entrar na chave.
    if (r.dimension === 'property' && propertyKey === '') continue
    const values = r.values.map((v) => v.trim()).filter((v) => v !== '')
    if (values.length === 0) continue
    rules.push(
      [r.dimension, propertyKey, r.operator, values.join('~')].join('::'),
    )
  }
  if (rules.length === 0) return ''
  return `${query.combinator}::${rules.join(' && ')}`
}

const cache = new Map<string, SelectionLike[]>()
let cacheModelKey = ''

/** Deita fora o que estiver em cache quando os modelos mudam. */
export function resetRuleCache(modelKey: string): void {
  if (cacheModelKey === modelKey) return
  cacheModelKey = modelKey
  cache.clear()
}

/** Corre a regra (ou devolve o que esta em cache). Uma regra vazia
 *  devolve zero elementos sem tocar nos modelos. */
export async function evaluateClashRule(
  query: FilterQuery | null | undefined,
  modelKey: string,
): Promise<ClashRuleOutcome> {
  resetRuleCache(modelKey)
  const key = ruleKey(query)
  if (key === '') return { targets: [], ms: 0, cached: true }
  const hit = cache.get(key)
  if (hit) return { targets: hit, ms: 0, cached: true }
  const t0 = performance.now()
  const targets = await evaluateFilterQuery(query as FilterQuery)
  const ms = performance.now() - t0
  cache.set(key, targets)
  return { targets, ms, cached: false }
}

